/**
 * A number that flashes green when it rises and red when it falls.
 *
 * This is the one piece of motion on the screen that carries information
 * rather than decorating a transition: on a page where a dozen prices update
 * independently, the flash is what tells you *which* one moved, without
 * reading any of them. Direction comes from comparing against the previous
 * render, so it is the tick that is being shown, not the day's change -- a
 * stock down 3% on the day still flashes green when it upticks.
 *
 * The flash is a background wash rather than a colour change on the text: the
 * text is already coloured by the day's change, and overriding that would
 * trade a persistent fact for a transient one.
 */
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useRef, useState } from "react";

type Direction = "up" | "down";

export interface FlashValueProps {
  /** The number to watch. Formatting is the caller's business. */
  value: number | null | undefined;
  children: React.ReactNode;
  className?: string;
}

/** Long enough to register, short enough not to still be lit on the next
 *  tick of a fast-moving symbol. */
const FLASH_MS = 420;

export function FlashValue({ value, children, className }: FlashValueProps) {
  const previous = useRef<number | null | undefined>(value);
  const [flash, setFlash] = useState<{ dir: Direction; id: number } | null>(null);
  const seq = useRef(0);

  useEffect(() => {
    const before = previous.current;
    previous.current = value;
    if (
      value === null || value === undefined ||
      before === null || before === undefined ||
      value === before
    ) {
      return;
    }
    // A new id on every tick so AnimatePresence restarts the wash rather than
    // letting a still-running one absorb the change.
    seq.current += 1;
    const id = seq.current;
    setFlash({ dir: value > before ? "up" : "down", id });
    const timer = window.setTimeout(() => {
      // Only clear if nothing newer has arrived, or a fast symbol would
      // extinguish its own later flashes.
      setFlash((f) => (f?.id === id ? null : f));
    }, FLASH_MS);
    return () => window.clearTimeout(timer);
  }, [value]);

  return (
    <span className={`flash-host ${className ?? ""}`}>
      <AnimatePresence>
        {flash && (
          <motion.span
            key={flash.id}
            className={`flash-wash ${flash.dir}`}
            initial={{ opacity: 0.85 }}
            animate={{ opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: FLASH_MS / 1000, ease: "easeOut" }}
          />
        )}
      </AnimatePresence>
      <span className="flash-text">{children}</span>
    </span>
  );
}
